import { useEffect, useMemo, useState } from 'react';
import {
  Modal,
  ModalContent,
  Button,
  Input,
  Avatar,
  Checkbox,
  Badge,
  EmptyState,
  CircularLoader,
  Pagination,
  SearchIcon,
  GroupIcon,
} from '@forge/common';
import { keycloakApi } from '@/api/keycloak';
import type { KeycloakUser } from '@/api/keycloak';

interface AddMemberDialogProps {
  groupName: string;
  existingMemberIds: string[];
  isSubmitting: boolean;
  onAdd: (userIds: string[]) => void;
  onClose: () => void;
}

const PAGE_SIZE = 8;

const AddMemberDialog = ({
  groupName,
  existingMemberIds,
  isSubmitting,
  onAdd,
  onClose,
}: AddMemberDialogProps) => {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [users, setUsers] = useState<KeycloakUser[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [selected, setSelected] = useState<Record<string, string>>({});

  // Debounce the search input so we are not hammering Keycloak on each keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError(false);
    keycloakApi
      .listUsers({
        search: debouncedSearch || undefined,
        first: (page - 1) * PAGE_SIZE,
        max: PAGE_SIZE,
      })
      .then((res) => {
        if (!active) return;
        setUsers(res.users);
        setTotal(res.total);
      })
      .catch(() => {
        if (!active) return;
        setUsers([]);
        setTotal(0);
        setLoadError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [debouncedSearch, page]);

  const existing = useMemo(
    () => new Set(existingMemberIds),
    [existingMemberIds],
  );

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const selectedCount = Object.keys(selected).length;

  const toggle = (user: KeycloakUser) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (next[user.id]) delete next[user.id];
      else next[user.id] = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
      return next;
    });
  };

  return (
    <Modal
      className="w-[44rem] max-w-[94vw]"
      title={`Add Members to ${groupName}`}
      onModalClose={onClose}
      onBackdropClick={onClose}
      footerLeft={
        <Button variant="tertiary" onClick={onClose}>
          Cancel
        </Button>
      }
      footerRight={
        <Button
          variant="primary"
          onClick={() => onAdd(Object.keys(selected))}
          disabled={selectedCount === 0 || isSubmitting}
        >
          {isSubmitting
            ? 'Adding...'
            : `Add ${selectedCount || ''} Member${selectedCount !== 1 ? 's' : ''}`}
        </Button>
      }
    >
      <ModalContent padded>
        <div className="flex flex-col gap-3">
          <Input
            id="add-member-search"
            placeholder="Search users by name or email..."
            prefix={<SearchIcon />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          {selectedCount > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-color-tertiary">
                {selectedCount} selected:
              </span>
              {Object.entries(selected).map(([id, name]) => (
                <Badge key={id} color="blue">
                  {name || id}
                </Badge>
              ))}
            </div>
          )}

          <div className="min-h-[20rem]">
            {loading ? (
              <div className="flex h-72 items-center justify-center">
                <CircularLoader />
              </div>
            ) : loadError ? (
              <EmptyState
                icon={<GroupIcon />}
                title="Couldn't load users"
                description="Something went wrong reaching the directory. Check your connection and try again."
              />
            ) : users.length === 0 ? (
              <EmptyState
                icon={<GroupIcon />}
                description="No users match your search"
              />
            ) : (
              <div className="flex flex-col rounded-lg border border-gray-200 dark:border-gray-700">
                {users.map((user) => {
                  const isMember = existing.has(user.id);
                  const isChecked = !!selected[user.id];
                  return (
                    <label
                      key={user.id}
                      className={`flex items-center gap-3 border-b border-gray-100 p-2.5 last:border-b-0 dark:border-gray-800 ${
                        isMember
                          ? 'cursor-not-allowed opacity-60'
                          : 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800'
                      }`}
                    >
                      <Checkbox
                        id={`add-member-${user.id}`}
                        checked={isChecked}
                        disabled={isMember}
                        onChange={() => !isMember && toggle(user)}
                      />
                      <Avatar
                        text={`${user.firstName?.[0] ?? ''}${user.lastName?.[0] ?? ''}`}
                        size="sm"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">
                          {user.firstName} {user.lastName}
                        </p>
                        <p className="truncate text-xs text-color-tertiary">
                          {user.email}
                        </p>
                      </div>
                      {isMember && <Badge color="gray">Already a member</Badge>}
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          {totalPages > 1 && (
            <div className="flex justify-center">
              <Pagination
                currentPage={page}
                totalItems={total}
                totalPages={totalPages}
                pageSize={PAGE_SIZE}
                onPageChange={setPage}
              />
            </div>
          )}
        </div>
      </ModalContent>
    </Modal>
  );
};

export default AddMemberDialog;
